/**
 * CarteraPage
 * Credits with outstanding balance (ACTIVE + DELINQUENT + DEFAULTED).
 * Uses the same real API as Creditos Activos, filtered to only show credits being collected.
 * Click on a row → Credit Detail (amortization + payment history)
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { PendienteCell } from '@/components/data-display/PendienteCell';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { DataTable } from '@/features/credit/components/DataTable';
import { useBackofficeCredits } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';

import { CreditLevel } from '@/types/credit.types';
import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';

// ─── Constants ──────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
  { label: 'Cartera', path: ROUTES.COLLECTION_CARTERA },
];

// Statuses that mean "credit has outstanding balance to collect"
const CARTERA_STATUSES = ['ACTIVE', 'DELINQUENT', 'DEFAULTED', 'DEFAULT'];

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'DELINQUENT':
      return 'warning';
    case 'DEFAULT':
    case 'DEFAULTED':
      return 'danger';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'Al corriente';
    case 'DELINQUENT': return 'En mora';
    case 'DEFAULT': case 'DEFAULTED': return 'Default';
    default: return status;
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr || dateStr === '-') return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

// ─── Columns ────────────────────────────────────────────────────────────────

const columns: ColumnDef<BackofficeCreditsListItem, unknown>[] = [
  {
    accessorKey: 'disbursed_at',
    header: 'Desembolso',
    cell: ({ getValue }) => formatDateShort(getValue<string>()),
  },
  {
    accessorKey: 'full_name',
    header: 'Nombre',
    cell: ({ getValue }) => getValue<string>() || <PendienteCell />,
  },
  {
    accessorKey: 'phone_number',
    header: 'Telefono',
    cell: ({ getValue }) => (
      <span className="tabular-nums">{getValue<string>()}</span>
    ),
  },
  {
    accessorKey: 'credit_level',
    header: 'Nivel',
    cell: ({ getValue }) => {
      const level = getValue<string>();
      const isValid = Object.values(CreditLevel).includes(level as CreditLevel);
      return isValid ? (
        <CreditLevelBadge level={level as CreditLevel} size="sm" />
      ) : (
        <span className="text-xs text-muted-foreground">{level}</span>
      );
    },
  },
  {
    accessorKey: 'amount_mxn',
    header: 'Monto',
    cell: ({ getValue }) => (
      <span className="tabular-nums">
        {formatCurrency(getValue<number>(), 'MXN', 'es-MX')}
      </span>
    ),
  },
  {
    accessorKey: 'weekly_payment_mxn',
    header: 'Pago Semanal',
    cell: ({ getValue }) => (
      <span className="tabular-nums">
        {formatCurrency(getValue<number>(), 'MXN', 'es-MX')}
      </span>
    ),
  },
  {
    accessorKey: 'weeks_paid',
    header: 'Progreso',
    cell: ({ row }) => {
      const paid = row.original.weeks_paid;
      const total = row.original.weeks_total;
      if (paid == null || total == null) return <PendienteCell />;
      return <span>{paid}/{total} sem</span>;
    },
  },
  {
    accessorKey: 'next_due_date',
    header: 'Prox. vencimiento',
    cell: ({ row }) => {
      const date = row.original.next_due_date;
      if (!date) return <PendienteCell />;
      return <span>{formatDateShort(date)}</span>;
    },
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ getValue }) => {
      const status = getValue<string>();
      return <Badge variant={getStatusVariant(status)}>{getStatusLabel(status)}</Badge>;
    },
  },
  {
    accessorKey: 'credit_id',
    header: 'ID',
    cell: ({ getValue }) => (
      <span className="font-mono text-xs" title={getValue<string>()}>
        {getValue<string>().slice(0, 8)}...
      </span>
    ),
  },
];

// ─── Filter Options ─────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'ACTIVE', label: 'Al corriente' },
  { value: 'DELINQUENT', label: 'En mora' },
  { value: 'DEFAULT', label: 'Default' },
];

// ─── Main Page ──────────────────────────────────────────────────────────────

export function CarteraPage() {
  const { credits, isLoading, isError, refetch } = useBackofficeCredits();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');

  // Filter to only credits with outstanding balance
  const carteraCredits = useMemo(() => {
    let result = credits.filter((c) => CARTERA_STATUSES.includes(c.status));
    if (statusFilter) {
      result = result.filter((c) => c.status === statusFilter);
    }
    return result;
  }, [credits, statusFilter]);

  const totalMora = credits.filter((c) => c.status === 'DELINQUENT' || c.status === 'DEFAULT' || c.status === 'DEFAULTED').length;
  const totalActive = credits.filter((c) => c.status === 'ACTIVE').length;
  const totalCartera = totalActive + totalMora;

  const handleRowClick = (row: BackofficeCreditsListItem) => {
    navigate(`/credit/detail/${row.credit_id}?directoryId=${row.directory_id}&from=cartera`);
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Cartera" helpKey="cartera" subtitle="Creditos con saldo pendiente" breadcrumbs={BREADCRUMBS} />
        <div className="space-y-4">
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={300} />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader title="Cartera" helpKey="cartera" subtitle="Creditos con saldo pendiente" breadcrumbs={BREADCRUMBS} />
        <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
          <h3 className="text-lg font-semibold text-foreground">Error al cargar la cartera</h3>
          <Button className="mt-4" onClick={() => refetch()}>
            <RefreshCw size={16} /> Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Cartera" helpKey="cartera"
        subtitle={`${totalCartera} creditos — ${totalActive} al corriente, ${totalMora} en mora`}
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            
            <Button onClick={() => refetch()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-4">
        <div className="flex flex-wrap gap-4">
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filtrar por estado"
          />
        </div>

        <DataTable
          columns={columns}
          data={carteraCredits}
          searchPlaceholder="Buscar por telefono o ID..."
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  );
}
